import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  LayoutDashboard,
  Users,
  LogOut,
  ShoppingBag,
  Utensils,
  Wallet,
  ClipboardCheck,
  FileText,
  SearchX,
  MousePointerClick,
  Share2,
  Settings,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api, toPage } from '../services/api';
import { cx, Avatar } from './ui';

const NAV_GROUPS = [
  {
    title: 'Tổng quan',
    items: [{ name: 'Dashboard', short: 'Tổng quan', href: '/dashboard', icon: LayoutDashboard }]
  },
  {
    title: 'Vận hành',
    items: [
      { name: 'Đối soát & duyệt đơn', short: 'Đối soát', href: '/approval', icon: ClipboardCheck },
      { name: 'Báo cáo đối soát Shopee', short: 'Báo cáo', href: '/shopee-validations', icon: FileText },
      { name: 'Thanh toán rút tiền', short: 'Rút tiền', href: '/withdrawals', icon: Wallet, badge: 'withdrawals' },
      { name: 'Đơn thất lạc', short: 'Thất lạc', href: '/lost-orders', icon: SearchX, badge: 'lost' }
    ]
  },
  {
    title: 'Đơn hàng',
    items: [
      { name: 'Đơn hàng', short: 'Đơn hàng', href: '/orders', icon: ShoppingBag },
      { name: 'Lượt click', href: '/clicks', icon: MousePointerClick },
      { name: 'Chia sẻ hoa hồng', href: '/share-commissions', icon: Share2 },
      { name: 'ShopeeFood', href: '/shopeefood', icon: Utensils }
    ]
  },
  {
    title: 'Người dùng',
    items: [{ name: 'Người dùng', href: '/users', icon: Users }]
  },
  {
    title: 'Hệ thống',
    items: [{ name: 'Cài đặt', href: '/settings', icon: Settings, badge: 'settings' }]
  }
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

// 4 mục chính trên thanh điều hướng dưới (mobile), mục thứ 5 là "Thêm"
const BOTTOM_ITEMS = ['/dashboard', '/approval', '/withdrawals', '/orders'].map((href) => ALL_ITEMS.find((i) => i.href === href));

// Việc đang chờ hiện trên thanh trên cùng (desktop)
const TOPBAR_TASKS = [
  { key: 'withdrawals', label: 'rút tiền chờ duyệt', href: '/withdrawals' },
  { key: 'lost', label: 'đơn thất lạc', href: '/lost-orders' }
];

const CountBadge = ({ value, className }) =>
  value > 0 ? (
    <span className={cx('tabular inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold leading-5 text-white', className)}>
      {value > 99 ? '99+' : value}
    </span>
  ) : null;

const Layout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Số việc đang chờ để hiện badge trên menu
  const { data: counts } = useQuery(
    'nav-counts',
    async () => {
      const [w, l, s] = await Promise.all([
        api.get('/admin/withdrawals', { params: { status: 'PENDING', limit: 1 } }).then(toPage),
        api.get('/admin/lost-order-claims', { params: { status: 'PENDING', limit: 1 } }).then(toPage),
        // Cookie Lazada bị từ chối: badge cảnh báo ở menu Cài đặt
        api.get('/admin/settings').then((res) => res.data.data).catch(() => null)
      ]);
      return {
        withdrawals: Number(w.summary?.PENDING?.count ?? w.pagination.total ?? 0),
        lost: Number(l.pagination.total || 0),
        settings: s?.lazada_cookie_status?.alert ? 1 : 0
      };
    },
    { refetchInterval: 60000, staleTime: 30000 }
  );

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  const currentItem = useMemo(
    () => ALL_ITEMS.find((i) => location.pathname === i.href || location.pathname.startsWith(`${i.href}/`)),
    [location.pathname]
  );
  const currentGroup = NAV_GROUPS.find((g) => g.items.includes(currentItem));

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navList = (onNavigate) => (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-3 [scrollbar-width:thin]">
      {NAV_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="mb-1 px-3 text-xs font-medium text-gray-500">{group.title}</p>
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cx(
                    'relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400',
                    isActive ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-gray-100'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-gold-400" />}
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    <span className="flex-1 truncate">{item.name}</span>
                    {item.badge && <CountBadge value={counts?.[item.badge]} />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );

  const userBlock = (
    <div className="border-t border-white/10 p-3">
      <div className="flex items-center gap-2.5 px-1">
        <Avatar name={user?.name} email={user?.email} className="bg-gold-400 text-gray-900" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-100">{user?.name}</p>
          <p className="truncate text-xs text-gray-400">{user?.email}</p>
        </div>
        <button type="button" onClick={handleLogout} className="rounded-md p-1.5 text-gray-400 hover:bg-white/10 hover:text-white" title="Đăng xuất" aria-label="Đăng xuất">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  const brand = (
    <Link to="/dashboard" className="flex h-14 items-center gap-2.5 px-5">
      <img src="/logo-icon.png" alt="" className="h-7 w-7" />
      <span className="text-[15px] font-semibold text-white">CaskBack</span>
      <span className="rounded bg-white/10 px-1.5 py-0.5 text-[11px] font-medium text-gray-300">Admin</span>
    </Link>
  );

  const moreBadge = (counts?.lost || 0);
  const tasks = TOPBAR_TASKS.filter((t) => counts?.[t.key] > 0);

  return (
    <div className="min-h-screen overflow-x-clip bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden bg-gray-900 lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        {brand}
        {navList()}
        {userBlock}
      </aside>

      {/* Drawer mobile ("Thêm") */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-gray-900/50" onClick={() => setDrawerOpen(false)} />
          <div className="absolute inset-y-0 left-0 flex w-[82%] max-w-xs flex-col bg-gray-900 shadow-xl">
            <div className="flex items-center justify-between pr-3">
              {brand}
              <button type="button" onClick={() => setDrawerOpen(false)} className="rounded-md p-2 text-gray-400 hover:bg-white/10" aria-label="Đóng menu">
                <X className="h-5 w-5" />
              </button>
            </div>
            {navList(() => setDrawerOpen(false))}
            <div className="pb-[env(safe-area-inset-bottom)]">{userBlock}</div>
          </div>
        </div>
      )}

      <div className="lg:pl-64">
        {/* Top bar mobile */}
        <header className="sticky top-0 z-40 flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4 lg:hidden">
          <button type="button" onClick={() => setDrawerOpen(true)} className="-ml-2 rounded-lg p-2 text-gray-600 hover:bg-gray-100" aria-label="Mở menu">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="flex-1 truncate text-base font-semibold text-gray-900">{currentItem?.name || 'CaskBack Admin'}</h1>
          <img src="/logo-icon.png" alt="" className="h-7 w-7" />
        </header>

        {/* Top bar desktop: vị trí hiện tại và việc đang chờ */}
        <header className="sticky top-0 z-40 hidden h-14 items-center justify-between gap-4 border-b border-gray-200 bg-white px-8 lg:flex">
          <div className="flex min-w-0 items-center gap-1.5 text-sm">
            {currentGroup && currentGroup.title !== currentItem?.name && (
              <>
                <span className="text-gray-500">{currentGroup.title}</span>
                <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
              </>
            )}
            <span className="truncate font-medium text-gray-900">{currentItem?.name}</span>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            {tasks.map((t) => (
              <Link key={t.key} to={t.href} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                <span className="tabular font-semibold text-gray-900">{counts[t.key]}</span> {t.label}
              </Link>
            ))}
          </div>
        </header>

        <main className="mx-auto w-full max-w-[90rem] px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom))] pt-4 md:px-6 lg:px-8 lg:pb-10 lg:pt-6">
          <Outlet />
        </main>
      </div>

      {/* Bottom navigation mobile */}
      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden">
        <div className="grid grid-cols-5">
          {BOTTOM_ITEMS.map((item) => (
            <NavLink
              key={item.href}
              to={item.href}
              className={({ isActive }) =>
                cx('relative flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium', isActive ? 'text-gray-900' : 'text-gray-500')
              }
            >
              <span className="relative">
                <item.icon className="h-6 w-6" />
                {item.badge && <CountBadge value={counts?.[item.badge]} className="absolute -right-3 -top-1.5" />}
              </span>
              {item.short || item.name}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="relative flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium text-gray-500"
          >
            <span className="relative">
              <Menu className="h-6 w-6" />
              {moreBadge > 0 && <span className="absolute -right-1 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500" />}
            </span>
            Thêm
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
